import type { ComponentProps, FormEvent, ReactElement, ReactNode, Ref } from 'react'

import { Button } from '@adverity/ds-component/button'
import type * as DsButton from '@adverity/ds-component/button'
import { Separator } from '@adverity/ds-component/separator'
import { isNullish, isString } from 'remeda'

import { useViewTransitionName } from '~/utils/use-view-transition-name'

import { Dialog } from '../dialog/dialog'
import { DialogActions } from '../dialog/dialog-actions'
import { DialogBackdrop } from '../dialog/dialog-backdrop'
import { DialogBody } from '../dialog/dialog-body'
import { DialogContainer } from '../dialog/dialog-container'
import { DialogFooter } from '../dialog/dialog-footer'
import { DialogHeader } from '../dialog/dialog-header'
import { DialogTitle } from '../dialog/dialog-title'
import { Flex } from '../flex/flex'

type ButtonBaseProps = DsButton.ButtonBaseProps

type FooterActionCommon = {
    label?: ReactNode
    prefix?: ReactNode
    suffix?: ReactNode
    tooltip?: ReactNode
    ref?: Ref<HTMLButtonElement>
}

type FooterActionButton = Omit<ButtonBaseProps, 'children' | 'asChild'> &
    FooterActionCommon & {
        asChild?: false
    }

// asChild renders the supplied element (typically an `<a href>`) styled as a Button.
// `label` is required so screen-readers still get a name even when children isn't a plain string.
type FooterActionAsChild = Omit<ButtonBaseProps, 'children' | 'asChild'> &
    FooterActionCommon & {
        asChild: true
        children: ReactElement
        label: string
    }

type FooterAction = FooterActionButton | FooterActionAsChild

type FooterButtonExtraProps = Pick<ComponentProps<typeof Button>, 'variant' | 'onClick' | 'type'>

const ModalFooterButton = ({
    action,
    defaultLabel,
    ...buttonProps
}: { action: FooterAction; defaultLabel: string } & FooterButtonExtraProps) => {
    if (action.asChild) {
        const { label, prefix, suffix, tooltip, asChild: asChildIgnored, children, ...rest } = action

        return (
            <Button {...buttonProps} {...rest} asChild prefix={prefix} suffix={suffix} tooltip={tooltip} label={label}>
                {children}
            </Button>
        )
    }

    const { label, prefix, suffix, tooltip, ...rest } = action

    if (isString(label)) {
        return (
            <Button {...buttonProps} {...rest} prefix={prefix} suffix={suffix} tooltip={tooltip}>
                {label}
            </Button>
        )
    }

    if (!isNullish(label)) {
        return (
            <Button {...buttonProps} {...rest} prefix={prefix} suffix={suffix} tooltip={tooltip} label={defaultLabel}>
                {label}
            </Button>
        )
    }

    return (
        <Button {...buttonProps} {...rest} prefix={prefix} suffix={suffix} tooltip={tooltip}>
            {defaultLabel}
        </Button>
    )
}

const sizeMapping = {
    small: 480,
    medium: 600,
    large: 920,
    full: '100%',
} as const

export type ModalDialogProps = {
    title: ReactNode
    subHeader?: ReactNode
    size?: 'small' | 'medium' | 'large' | 'full'
    additionalActions?: ReactNode
    cancelAction?: FooterAction | null
    applyAction?: FooterAction | null
    children: ReactNode
    onClose: () => void
    disablePortal?: boolean
    disableFocusTrap?: boolean
    // TODO: Fix signature when https://github.com/pmedianetwork/design-system/issues/1528 is finished.
    // The HTMLElement is a wrong generic type, it should be HTMLFormElement.
    // For further information read this thread: https://github.com/pmedianetwork/design-system/pull/2413#discussion_r591355275
    onSubmit?: (event: FormEvent<HTMLElement>, data: FormData) => void
} & Pick<ComponentProps<typeof Dialog>, 'role'>

// oxlint-disable-next-line complexity
export const ModalDialog = ({
    title,
    subHeader,
    size = 'medium',
    additionalActions,
    cancelAction = { label: 'Cancel' },
    applyAction = { label: 'Apply' },
    disablePortal,
    disableFocusTrap,
    onClose,
    children,
    onSubmit,
    role,
    // oxlint-disable-next-line complexity
}: ModalDialogProps) => {
    const viewTransitionName = useViewTransitionName()

    return (
        <DialogContainer disablePortal={disablePortal}>
            <DialogBackdrop
                css={{
                    // We need to give the backdrop a viewTransition name so it stacks nicely on top of others opened modals/drawers
                    viewTransitionName,
                }}
            >
                <Flex height="100%" justifyContent="center" alignItems="center" paddingY="x-small" data-backdrop>
                    <Dialog
                        css={{
                            display: 'flex',
                            flexDirection: 'column',
                            maxHeight: '90vh',
                        }}
                        preventScroll
                        as="form"
                        autoFocus
                        contain={!disableFocusTrap}
                        position="relative"
                        onClose={onClose}
                        marginY="auto"
                        marginX={['x-small', 'small', 'large']}
                        width={['auto', sizeMapping[size]]}
                        role={role}
                        isOutsideDismissable={false}
                        onSubmit={(event) => {
                            event.preventDefault()
                            onSubmit?.(event, new FormData(event.target as HTMLFormElement))
                        }}
                        onKeyDown={(event) => {
                            if (event.code === 'Escape') {
                                event.stopPropagation()
                            }
                        }}
                    >
                        <DialogHeader onClose={onClose}>
                            <DialogTitle>{title}</DialogTitle>
                        </DialogHeader>
                        <Separator />
                        {subHeader}
                        <DialogBody scrollableContent>{children}</DialogBody>
                        <Separator />
                        {(!!additionalActions || !!cancelAction || !!applyAction) && (
                            <DialogFooter>
                                <DialogActions additionalActions={additionalActions}>
                                    {cancelAction && (
                                        <ModalFooterButton
                                            action={cancelAction}
                                            defaultLabel="Cancel"
                                            variant="secondary"
                                            onClick={onClose}
                                        />
                                    )}
                                    {applyAction && (
                                        <ModalFooterButton action={applyAction} defaultLabel="Apply" type="submit" />
                                    )}
                                </DialogActions>
                            </DialogFooter>
                        )}
                    </Dialog>
                </Flex>
            </DialogBackdrop>
        </DialogContainer>
    )
}

ModalDialog.displayName = 'ModalDialog'
